/**
 * Dashboard Page - Overview of user's vaults and LOCs
 */

import { useAccount } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { Link } from 'react-router-dom'
import { SEO, generateBreadcrumbSchema } from '../components/common/SEO'
import { Card, CardContent, CardHeader, CardTitle } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { useUserLOCsFromEvents } from '../hooks/useLOCEvents'
import { useAccountCollateralBalance } from '../hooks/useVault'
import { useTokenPrices } from '../hooks/useTokenPrice'
import { formatAddress } from '../utils/format'
import { formatUnits } from 'viem'
import { SUPPORTED_TOKENS } from '@contracts/tokens'
import { 
  WalletConnectIllustration,
  CoinsLockIllustration,
  BitcoinHandsWalletIllustration,
  BlockSecureIllustration
} from '@bitcoin-com/react-components/src/components/mini-illustrations'
import { InfoBox } from '../components/common/InfoBox'
import { LiquidationRiskIndicator } from '../components/vault/LiquidationRiskIndicator'
import { HowItWorksFlow, StepProgress } from '../components/common/HowItWorksFlow'
import { useAllAccountCollateralBalances as _useAllAccountCollateralBalances } from '../hooks/useVault'
import { MINIMUM_COLLATERAL_USD } from '../config/constants'

export function DashboardPage() {
  const { address, isConnected } = useAccount()
  const { openConnectModal } = useConnectModal()
  // Use event-based LOC query since contract lacks getUserLOCs
  const { locs: locEvents, isLoading: locsLoading } = useUserLOCsFromEvents(address)
  const locIds = locEvents.map(loc => loc.id)
  
  // Note: Anvil uses account-based collateral, not vault IDs
  const hasAccount = !!address
  const vaultsLoading = false // No vaults in Anvil, using account-based system

  // Fetch prices for all supported tokens
  const { data: prices } = useTokenPrices(Object.values(SUPPORTED_TOKENS))
  
  // Fetch collateral balances for each supported token (must call hooks at top level)
  const wethBalance = useAccountCollateralBalance(address, SUPPORTED_TOKENS.WETH.address as `0x${string}`)
  const usdcBalance = useAccountCollateralBalance(address, SUPPORTED_TOKENS.USDC.address as `0x${string}`)
  const wbtcBalance = useAccountCollateralBalance(address, SUPPORTED_TOKENS.wBTC.address as `0x${string}`)
  const cbbtcBalance = useAccountCollateralBalance(address, SUPPORTED_TOKENS.cbBTC.address as `0x${string}`)
  const wstethBalance = useAccountCollateralBalance(address, SUPPORTED_TOKENS.wstETH.address as `0x${string}`)

  // Calculate total collateral value in USD
  let totalCollateralValueUSD = 0
  const isLoadingBalances = wethBalance.isLoading || usdcBalance.isLoading || wbtcBalance.isLoading || 
                           cbbtcBalance.isLoading || wstethBalance.isLoading

  const balanceData = [
    { token: SUPPORTED_TOKENS.WETH, balance: wethBalance.availableBalance || 0n },
    { token: SUPPORTED_TOKENS.USDC, balance: usdcBalance.availableBalance || 0n },
    { token: SUPPORTED_TOKENS.wBTC, balance: wbtcBalance.availableBalance || 0n },
    { token: SUPPORTED_TOKENS.cbBTC, balance: cbbtcBalance.availableBalance || 0n },
    { token: SUPPORTED_TOKENS.wstETH, balance: wstethBalance.availableBalance || 0n },
  ]

  balanceData.forEach(({ token, balance }) => {
    if (balance > 0n && prices) {
      const balanceFloat = parseFloat(formatUnits(balance, token.decimals))
      const price = prices[token.symbol] || 0
      const value = balanceFloat * price
      console.log(`💰 [Dashboard] ${token.symbol}:`, {
        balance: balanceFloat,
        price,
        value
      })
      totalCollateralValueUSD += value
    }
  })

  console.log(`💰 [Dashboard] Total Collateral: $${totalCollateralValueUSD.toFixed(2)}`)

  const hasLowCollateral = totalCollateralValueUSD > 0 && totalCollateralValueUSD < MINIMUM_COLLATERAL_USD
  const availableCredit = totalCollateralValueUSD * 0.75 // 75% LTV
  
  // Estimate total LOC value (in production, fetch actual values)
  // Assuming LOCs are using ~50% of collateral on average
  const hasActiveLOCs = (locIds?.length || 0) > 0
  const estimatedLOCValueUSD = hasActiveLOCs ? totalCollateralValueUSD * 0.5 : 0

  // Calculate user progress through the flow
  const userProgress: StepProgress = {
    fundVault: totalCollateralValueUSD >= MINIMUM_COLLATERAL_USD, // Has deposited sufficient collateral
    issueLOC: hasActiveLOCs, // Has issued at least one LOC
    applyLoan: false, // TODO: Track loan requests (for now false)
    loanStatus: undefined // TODO: Track loan status
  }

  const dashboardBreadcrumb = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Dashboard', url: '/dashboard' },
  ])

  if (!isConnected) {
    return (
      <>
        <SEO
          title="Dashboard"
          description="View your crypto vaults, collateral balances, active Letters of Credit, and loan requests all in one place."
          canonical="/dashboard"
          jsonLd={dashboardBreadcrumb}
        />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-l">
              <div className="w-[96px] h-[96px]">
                <WalletConnectIllustration className="w-full h-full" />
              </div>
            </div>
            <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-s">
              Connect Your Wallet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Please connect your wallet to view your dashboard and manage your vaults and LOCs.
            </p>
            <Button size="lg" onClick={openConnectModal}>Connect Wallet</Button>
          </div>
        </div>
      </>
    )
  }

  const stats = [
    {
      label: 'Collateral Account',
      value: hasAccount ? 'Active' : 'None',
      link: '/vaults',
      color: 'text-brand-orange',
      icon: BitcoinHandsWalletIllustration,
      bgGradient: 'from-brand-orange/20 to-brand-orange/10',
    },
    {
      label: 'Active LOCs',
      value: locIds?.length || 0,
      link: '/locs',
      color: 'text-brand-blue',
      icon: BlockSecureIllustration,
      bgGradient: 'from-brand-blue/20 to-brand-blue/10',
    },
    {
      label: 'Total Collateral',
      value: isLoadingBalances ? '...' : totalCollateralValueUSD > 0 ? `$${totalCollateralValueUSD.toFixed(2)}` : '$0',
      link: '/vaults',
      color: hasLowCollateral ? 'text-yellow-600' : 'text-green-600',
      icon: CoinsLockIllustration,
      bgGradient: hasLowCollateral ? 'from-yellow-200/30 to-yellow-100/20' : 'from-green-200/30 to-green-100/20',
    },
    {
      label: 'Available Credit',
      value: isLoadingBalances ? '...' : availableCredit > 0 ? `$${availableCredit.toFixed(2)}` : '$0',
      link: '/locs',
      color: 'text-purple-600',
      icon: WalletConnectIllustration,
      bgGradient: 'from-purple-200/30 to-purple-100/20',
    },
  ]

  return (
    <>
      <SEO
        title="Dashboard"
        description="View your crypto vaults, collateral balances, active Letters of Credit, and loan requests all in one place."
        canonical="/dashboard"
        jsonLd={dashboardBreadcrumb}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header - Bitcoin.com React CL Style */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
            <div>
              <h1 className="font-display font-black text-heading-xl uppercase text-text-primary mb-2">
                Dashboard
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Welcome back! Here's an overview of your account.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Link to="/vaults" className="w-full sm:w-auto">
                <Button size="lg" className="inline-flex items-center justify-center w-full sm:w-auto min-h-[48px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Make Deposit
                </Button>
              </Link>
              <Link to="/locs" className="w-full sm:w-auto">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto min-h-[48px]">Issue LOC</Button>
              </Link>
            </div>
          </div>

        {/* Low Collateral Warning */}
        {hasLowCollateral && (
          <InfoBox variant="warning" title="Low Collateral Balance">
            <p className="text-sm">
              You need at least <strong>${MINIMUM_COLLATERAL_USD}</strong> in collateral to request loans. 
              Current balance: <strong>${totalCollateralValueUSD.toFixed(2)}</strong>
            </p>
            <p className="text-sm mt-2">
              Deposit <strong>${(MINIMUM_COLLATERAL_USD - totalCollateralValueUSD).toFixed(2)}</strong> more to unlock loan applications.
            </p>
          </InfoBox>
        )}

        {/* Stats Grid - Bitcoin.com React CL Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => {
            const IconComponent = stat.icon
            return (
              <Link key={stat.label} to={stat.link}>
                <Card interactive>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        {stat.label}
                      </div>
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.bgGradient} flex items-center justify-center p-2`}>
                        <IconComponent className="w-full h-full" />
                      </div>
                    </div>
                    <div className={`text-4xl font-bold font-mono ${stat.color}`}>
                      {vaultsLoading || locsLoading || isLoadingBalances ? (
                        <div className="spinner h-8 w-8" />
                      ) : (
                        stat.value
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>

        {/* Liquidation Risk Indicator */}
        {hasActiveLOCs && totalCollateralValueUSD > 0 && (
          <LiquidationRiskIndicator 
            collateralValueUSD={totalCollateralValueUSD}
            locValueUSD={estimatedLOCValueUSD}
            showRecommendations={true}
          />
        )}

        {/* How It Works - Interactive Journey Guide */}
        <HowItWorksFlow 
          variant="compact" 
          progress={userProgress}
          showProgress={true}
          clickable={true}
        />

          {/* Recent Activity */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Vaults</CardTitle>
                  <Link to="/vaults" className="text-sm text-brand-orange hover:underline">
                    View All
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {vaultsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner h-8 w-8" />
                  </div>
                ) : hasAccount ? (
                  <div className="space-y-3">
                    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            Collateral Account
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                            {formatAddress(address || '')}
                          </div>
                        </div>
                        <Link to="/vaults">
                          <Button size="sm" variant="ghost">View Details</Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                    <p>No collateral account yet.</p>
                    <Link to="/vaults">
                      <Button size="sm" className="mt-4">
                        Make Your First Deposit
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent LOCs</CardTitle>
                  <Link to="/locs" className="text-sm text-brand-orange hover:underline">
                    View All
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {locsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner h-8 w-8" />
                  </div>
                ) : locIds && locIds.length > 0 ? (
                  <div className="space-y-3">
                    {locIds.slice(0, 3).map((locId) => (
                      <div
                        key={locId.toString()}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              LOC #{locId.toString()}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Click to view details
                            </div>
                          </div>
                          <Link to={`/locs/${locId}`}>
                            <Button size="sm" variant="ghost">View</Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                    <p>No LOCs yet.</p>
                    <Link to="/locs">
                      <Button size="sm" className="mt-4">
                        Issue Your First LOC
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}

